import { useEffect, useMemo, useRef, useState } from 'react';
import { Cpu, Moon, AlertTriangle, ArrowRight, ChevronLeft, ChevronsUp, Download, PawPrint, RotateCcw, ScrollText, Zap, ZoomIn, ZoomOut } from 'lucide-react';
import { createPortal } from 'react-dom';
import './CharacterCreator.css';
import type { ContentVersion } from './content/sources';
import { CREATION_STEPS, SheetSection } from './content/sheetText';
import { buildCatalog, Catalog, classFeatures } from './catalog';
import { BlankPart, Character, emptyCharacter, fightsInStances, isUntouched, markTrack, missingParts, missingSteps } from './model';
import { levelOf, levelUpProblems, MAX_LEVEL } from './leveling';
import { hasCompanion } from './companion';
import Builder, { EDIT_STEP, STEP_BODIES } from './Builder';
import { ShowNeeded } from './BuilderParts';
import { ElementFields, PatronFields, TransformationPicker } from './StepsChoose';
import { MotherboardPanels } from './MotherboardPanels';
import QuickBuild from './QuickBuild';
import Dialog from './Dialog';
import SavePdfDialog from './SavePdfDialog';
import LevelUpFlow from './LevelUpFlow';
import BeastformDialog from './BeastformDialog';
import CharacterDocument, { SheetStyle } from './CharacterDocument';
import type { ClassChoice } from './SheetParts';

type Mode = 'home' | 'quick' | 'build' | 'sheet' | 'blank';

/** Where one character is kept (on the site, one entry of this device's list: `characterStore`). */
export interface CharacterStore {
  load: () => Character | null;
  /** False when the host could not keep it. */
  save: (c: Character) => boolean;
  remove: () => void;
}

/**
 * Saves the PDF copy's pages (letter size, laid out off screen) as one PDF file named `fileName`.
 * Without it, Save as PDF opens the browser's print window; it also opens when this fails.
 */
export type SavePdf = (pages: HTMLElement[], fileName: string) => Promise<void>;

/** The built-in Save as PDF: a vector PDF written in the browser (pdf/), loaded only when a sheet is saved. */
const vectorPdf: SavePdf = (pages, fileName) => import('./pdf').then((pdf) => pdf.downloadPdf(pages, fileName));

interface Props {
  /** Which expansion versions to show (the site always passes 'hf'). */
  version: ContentVersion;
  store: CharacterStore;
  /** Back to the list of characters: a created character opens on its sheet, and Back returns there. */
  onHome: () => void;
  /** Opens the builder with this class when no character is kept. */
  startClass?: string;
  /** Shows this class's blank sheet to print, keeping nothing. */
  blankClass?: string;
  /** Back from the blank sheet. */
  onBlankBack?: () => void;
  savePdf?: SavePdf;
}

/** The whole character creation: Quick or Detailed builder, drafts, the sheet, level up and PDF. */
export default function CharacterCreator({ version, store, onHome, startClass: start, blankClass: blank, onBlankBack = () => {}, savePdf }: Props) {
  const catalog = useMemo(() => buildCatalog(version), [version]);
  const blankClass = catalog.cls(blank ?? '')?.name;
  const startClass = catalog.cls(start ?? '')?.name;

  const [character, setCharacter] = useState<Character | null>(
    () => store.load() ?? (startClass ? emptyCharacter(startClass) : null),
  );
  const [mode, setMode] = useState<Mode>(() => {
    const saved = store.load();
    if (blankClass) return 'blank';
    // An unfinished character reopens in the builder it was left in.
    if (saved && !saved.finished) return saved.progress?.mode ?? 'build';
    if (saved) return 'sheet';
    return startClass ? 'build' : 'home';
  });
  const [saveFailed, setSaveFailed] = useState(false);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    if (!character) return;
    // Nothing chosen yet: leaving now is the same as starting over, so nothing is kept.
    if (isUntouched(character)) store.remove();
    else setSaveFailed(!store.save(character));
  }, [character, store]);

  const startNew = (how: 'quick' | 'build') => {
    setCharacter({ ...emptyCharacter(), progress: { mode: how, step: 1 } });
    setMode(how);
  };

  const toDetailed = () => {
    if (character) setCharacter({ ...character, progress: { mode: 'build', step: 1 } });
    setMode('build');
  };

  const finish = () => {
    if (character) setCharacter({ ...character, finished: true, progress: undefined });
    setMode('sheet');
  };

  const startOver = () => {
    store.remove();
    setCharacter(null);
    setRestarting(false);
    setMode('home');
  };

  return (
    <div className="cc-creator">
      {saveFailed && <p className="cc-warning" role="alert">This browser won't let the site save your character. Keep this tab open or save it as a PDF.</p>}
      {mode !== 'sheet' && mode !== 'blank' && (
        <button type="button" className="back-btn cc-back" onClick={onHome}>
          <ChevronLeft size={16} aria-hidden="true" />Your characters
        </button>
      )}

      {(mode === 'build' || mode === 'quick') && character && !character.finished && (
        <div className="cc-draft">
          <p className="cc-note">Saved on this device as you go. It becomes your character when you press Create character.</p>
          <button type="button" className="back-btn" onClick={() => setRestarting(true)}>
            <RotateCcw size={16} aria-hidden="true" />Start over
          </button>
        </div>
      )}
      {restarting && (
        <Dialog
          title="Start over?"
          role="alertdialog"
          onClose={() => setRestarting(false)}
          footer={
            <>
              <button type="button" className="cb-btn" onClick={() => setRestarting(false)}>Keep building</button>
              <button type="button" className="cb-btn cb-btn--primary" onClick={startOver}>
                <RotateCcw size={16} aria-hidden="true" />Start over
              </button>
            </>
          }
        >
          <p className="cb-pdf__lead">This clears every choice made so far and goes back to choosing a Quick or Detailed character.</p>
        </Dialog>
      )}

      {mode === 'build' && character && (
        <Builder
          character={character}
          catalog={catalog}
          onChange={setCharacter}
          onFinish={finish}
        />
      )}

      {mode === 'quick' && character && (
        <QuickBuild character={character} catalog={catalog} onChange={setCharacter} onFinish={finish} onDetailed={toDetailed} />
      )}

      {mode === 'sheet' && character && (
        <SheetView character={character} catalog={catalog} onBack={onHome} onChange={setCharacter} savePdf={savePdf} />
      )}

      {mode === 'blank' && blankClass && (
        <SheetView character={emptyCharacter(blankClass)} catalog={catalog} onBack={onBlankBack} savePdf={savePdf} />
      )}

      {mode === 'home' && <Intro onStart={startNew} />}
    </div>
  );
}

function Intro({ onStart }: { onStart: (how: 'quick' | 'build') => void }) {
  return (
    <section className="cc-intro">
      <p className="cc-intro__lead">
        Every class, subclass, ancestry, community, weapon, armor and domain card is already here: you choose, and your
        character sheet fills itself in. When you finish, save it as a PDF in this site's style or the classic Daggerheart look.
      </p>
      <div className="cc-modes">
        <section className="cc-mode" aria-labelledby="cc-mode-quick">
          <span className="cc-mode__icon"><Zap size={22} aria-hidden="true" /></span>
          <h2 id="cc-mode-quick" className="cc-mode__title">Quick</h2>
          <p>A few choices on one page: class, heritage, name, Experiences, gear and domain cards. Traits and gear use your class's suggestions.</p>
          <button type="button" className="cb-btn cb-btn--primary" onClick={() => onStart('quick')}>
            Quick character<ArrowRight size={16} aria-hidden="true" />
          </button>
        </section>
        <section className="cc-mode" aria-labelledby="cc-mode-detailed">
          <span className="cc-mode__icon"><ScrollText size={22} aria-hidden="true" /></span>
          <h2 id="cc-mode-detailed" className="cc-mode__title">Detailed</h2>
          <p>All nine steps of the official guide below, with the rules for every choice and your sheet filling in beside you.</p>
          <button type="button" className="cb-btn cb-btn--primary" onClick={() => onStart('build')}>
            Detailed character<ArrowRight size={16} aria-hidden="true" />
          </button>
        </section>
      </div>
      <ol className="cc-overview" aria-label="The nine steps">
        {CREATION_STEPS.map((step, i) => (
          <li key={step.title} className="cc-overview__step" title={step.text[0]}>
            <span className="cc-overview__num"><span>{i + 1}</span></span>
            <span className="cc-overview__title">{step.title}</span>
          </li>
        ))}
      </ol>
      <p className="cc-note">Your character is saved on this device as you build it.</p>
    </section>
  );
}

/** Save as PDF's reminder of how a Martial Artist's Martial Stances page prints. */
function stanceNote(c: Character, catalog: Catalog) {
  if (!catalog.cls(c.className)?.subclasses?.[c.subclass]?.martialForm) return undefined;
  return c.stanceSheet === 'full'
    ? 'Martial Stances prints as a full page. You can switch it to a half page above that page.'
    : 'Martial Stances prints as a half page, to cut out as a side sheet. You can switch it to a full page above that page.';
}

interface SheetViewProps {
  character: Character;
  catalog: Catalog;
  onBack: () => void;
  /** Edits from the sheet: marks, gear edits and level ups. Left out for a blank sheet. */
  onChange?: (c: Character) => void;
  savePdf?: SavePdf;
}

function SheetView({ character, catalog, onBack, onChange, savePdf }: SheetViewProps) {
  const [style, setStyle] = useState<SheetStyle>('site');
  const [zoom, setZoom] = useState<'fit' | 'actual'>('fit');
  const [printing, setPrinting] = useState(false);
  const printCopy = useRef<HTMLDivElement>(null);
  const [askingBlank, setAskingBlank] = useState(false);
  // The parts the last PDF left empty, picked again when customizing.
  const [blank, setBlank] = useState<BlankPart[]>([]);
  const [editing, setEditing] = useState<number | null>(null);
  // 'edit' reopens the last level to make the choices it was finished without.
  const [levelingUp, setLevelingUp] = useState<'new' | 'edit' | null>(null);
  const [shifting, setShifting] = useState<string | null>(null);
  const [transforming, setTransforming] = useState(false);
  const [tinkering, setTinkering] = useState(false);
  const [choosing, setChoosing] = useState<ClassChoice | null>(null);
  const level = levelOf(character);
  const { beastform, ...unshifted } = character;

  // Save the PDF copy as a file, or print only that copy; its title becomes the PDF's file name.
  useEffect(() => {
    if (!printing) return;
    const title = document.title;
    const done = () => setPrinting(false);
    document.title = `${character.name || character.className} - Daggerheart character`;
    document.body.classList.add('cs-printing');
    window.addEventListener('afterprint', done);
    const pages = Array.from(printCopy.current?.querySelectorAll<HTMLElement>('.cs-page') ?? []);
    // Long texts are fitted again once the sheet fonts load, so wait for them first.
    Promise.resolve(document.fonts?.ready)
      .then(() => (savePdf ?? vectorPdf)(pages, `${document.title}.pdf`))
      .then(done, () => window.print());
    return () => {
      window.removeEventListener('afterprint', done);
      document.body.classList.remove('cs-printing');
      document.title = title;
    };
  }, [printing, character.name, character.className, savePdf]);

  const openEditor = (section: SheetSection) => (section === 'level' ? setLevelingUp('new') : setEditing(EDIT_STEP[section] ?? null));
  const Body = editing ? STEP_BODIES[editing - 1] : null;

  return (
    <div className="cc-sheet-view">
      <div className="cc-toolbar">
        <button type="button" className="back-btn" onClick={onBack}>
          <ChevronLeft size={16} aria-hidden="true" />Back
        </button>
        {onChange && level < MAX_LEVEL && (
          <button type="button" className="back-btn" onClick={() => setLevelingUp('new')}>
            <ChevronsUp size={16} aria-hidden="true" />Level up to {level + 1}
          </button>
        )}
        {onChange && (
          <button type="button" className="back-btn" onClick={() => setTransforming(true)}>
            <Moon size={16} aria-hidden="true" />Transformation
          </button>
        )}
        {onChange && character.useIkonis && (
          <button type="button" className="back-btn" onClick={() => setTinkering(true)}>
            <Cpu size={16} aria-hidden="true" />Motherboard
          </button>
        )}
        {onChange && !beastform && classFeatures(catalog.cls(character.className)).some((f) => f.name === 'Beastform') && (
          <button type="button" className="back-btn" onClick={() => setShifting('')}>
            <PawPrint size={16} aria-hidden="true" />Beastform
          </button>
        )}
        {onChange && beastform && (
          <button type="button" className="back-btn cc-toolbar__beast" onClick={() => onChange(unshifted)}>
            <PawPrint size={16} aria-hidden="true" />Drop out of {beastform.name}
          </button>
        )}
        {onChange && <MissingInfo character={character} companion={hasCompanion(character, catalog)} onFix={setEditing} onFixLevel={() => setLevelingUp('edit')} />}
        <div className="cc-toolbar__end">
          <div className="cb-switch" role="group" aria-label="Sheet style">
            <button type="button" className="back-btn" aria-pressed={style === 'site'} onClick={() => setStyle('site')}>
              Site style
            </button>
            <button type="button" className="back-btn" aria-pressed={style === 'classic'} onClick={() => setStyle('classic')}>
              Daggerheart style
            </button>
          </div>
          <button type="button" className="back-btn" aria-pressed={zoom === 'actual'} onClick={() => setZoom(zoom === 'fit' ? 'actual' : 'fit')}>
            {zoom === 'fit' ? <ZoomIn size={16} aria-hidden="true" /> : <ZoomOut size={16} aria-hidden="true" />}
            {zoom === 'fit' ? 'Zoom in' : 'Fit to screen'}
          </button>
          <button type="button" className="back-btn cc-toolbar__primary" disabled={printing} onClick={() => setAskingBlank(true)}>
            <Download size={16} aria-hidden="true" />{printing ? 'Saving PDF...' : 'Save as PDF'}
          </button>
        </div>
      </div>
      {onChange && (
        <p className="cc-note">
          Tap the boxes to mark Hit Points, Stress, Hope, Armor Slots and gold as you play. Use a gear to change that part of the
          sheet, or the arrows by Level to level up. Everything is saved on this device.
        </p>
      )}
      <CharacterDocument
        character={character}
        catalog={catalog}
        style={style}
        zoom={zoom}
        onMark={onChange && ((track, count) => onChange(markTrack(character, catalog, track, count)))}
        onEdit={onChange && openEditor}
        onBeastform={onChange && setShifting}
        onChoose={onChange && setChoosing}
        onSet={onChange && ((patch) => onChange({ ...character, ...patch }))}
      />
      {printing && createPortal(
        <div ref={printCopy} className="cs-print"><CharacterDocument character={character} catalog={catalog} style={style} blank={blank} /></div>,
        document.body,
      )}
      {askingBlank && (
        <SavePdfDialog
          initial={blank}
          note={stanceNote(character, catalog)}
          onClose={() => setAskingBlank(false)}
          onSave={(next) => {
            setBlank(next);
            setAskingBlank(false);
            setPrinting(true);
          }}
        />
      )}
      {onChange && Body && editing && (
        <Dialog
          title={CREATION_STEPS[editing - 1].title}
          onClose={() => setEditing(null)}
          footer={<button type="button" className="cb-btn cb-btn--primary" onClick={() => setEditing(null)}>Done</button>}
        >
          {/* Opened from the sheet, a step lights what it still needs */}
          <ShowNeeded.Provider value>
            <Body c={character} catalog={catalog} set={(patch) => onChange({ ...character, ...patch })} />
          </ShowNeeded.Provider>
        </Dialog>
      )}
      {onChange && shifting !== null && (
        <BeastformDialog
          character={character}
          catalog={catalog}
          form={shifting}
          onClose={() => setShifting(null)}
          onDone={(next) => {
            onChange(next);
            setShifting(null);
          }}
        />
      )}
      {onChange && tinkering && (
        <Dialog
          title="Motherboard"
          onClose={() => setTinkering(false)}
          footer={<button type="button" className="cb-btn cb-btn--primary" onClick={() => setTinkering(false)}>Done</button>}
        >
          <MotherboardPanels c={character} set={(patch) => onChange({ ...character, ...patch })} />
        </Dialog>
      )}
      {onChange && choosing && (
        <Dialog
          title={choosing === 'patron' ? 'Your patron' : 'Your element'}
          onClose={() => setChoosing(null)}
          footer={<button type="button" className="cb-btn cb-btn--primary" onClick={() => setChoosing(null)}>Done</button>}
        >
          {choosing === 'patron'
            ? <PatronFields c={character} set={(patch) => onChange({ ...character, ...patch })} />
            : <ElementFields c={character} set={(patch) => onChange({ ...character, ...patch })} />}
        </Dialog>
      )}
      {onChange && transforming && (
        <Dialog
          title="Transformation"
          onClose={() => setTransforming(false)}
          footer={<button type="button" className="cb-btn cb-btn--primary" onClick={() => setTransforming(false)}>Done</button>}
        >
          <TransformationPicker c={character} catalog={catalog} set={(patch) => onChange({ ...character, ...patch })} onDone={() => setTransforming(false)} />
        </Dialog>
      )}
      {onChange && levelingUp && (
        <LevelUpFlow
          character={character}
          catalog={catalog}
          edit={levelingUp === 'edit'}
          onClose={() => setLevelingUp(null)}
          onDone={(next) => {
            onChange(next);
            setLevelingUp(null);
          }}
        />
      )}
    </div>
  );
}

interface MissingInfoProps {
  character: Character;
  /** A Beastbound companion's level choices count too. */
  companion: boolean;
  onFix: (step: number) => void;
  onFixLevel: () => void;
}

/** A small warning that lists the creation steps and level choices still missing, with a way to fix each it can. */
function MissingInfo({ character, companion, onFix, onFixLevel }: MissingInfoProps) {
  const [open, setOpen] = useState(false);
  const lastLevel = levelOf(character);
  const missing = [
    ...missingSteps(character).map((n) => ({ key: `step-${n}`, text: `${n}. ${CREATION_STEPS[n - 1].title}: ${missingParts(n, character).join(', ')}`, fix: () => onFix(n) })),
    ...character.levelUps
      .map((l) => ({ l, problems: levelUpProblems(l, character.multiclass, companion, fightsInStances(character)) }))
      .filter(({ problems }) => problems.length)
      // Only the last level can be reopened; an earlier one is fixed by undoing the levels after it.
      .map(({ l, problems }) => ({ key: `level-${l.level}`, text: `Level ${l.level}: ${problems.join(' ')}`, fix: l.level === lastLevel ? onFixLevel : undefined })),
  ];
  if (!missing.length) return null;
  return (
    <div className="cc-missing">
      <button
        type="button"
        className="back-btn cc-missing__icon"
        aria-label="Missing information"
        aria-expanded={open}
        title="Missing information"
        onClick={() => setOpen(!open)}
      >
        <AlertTriangle size={16} aria-hidden="true" />
        <span aria-hidden="true">{missing.length}</span>
      </button>
      {open && (
        <ul className="cc-missing__list" aria-label="Missing information">
          {missing.map((m) => (
            <li key={m.key}>
              <span>{m.text}</span>
              {m.fix && (
                <button
                  type="button"
                  className="cb-btn"
                  onClick={() => {
                    setOpen(false);
                    m.fix?.();
                  }}
                >
                  Fix
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
